import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { LocationQueryAllSchema, LocationQuerySchema, LocationUpdateSchema } from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PREFERRED_LOCATIONS'] });
};

export const usePreferredLocationsMutation: UseControlledMutation<LocationQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      const res = await apiClient.get<LocationQueryAllSchema>({
        endpoint: '/api/v1/preferred-locations',
        params: { search, 'filter[status]': 'active', per_page },
      });
      return orderBy(res?.data ?? [], ['name']) || [];
    },
  });

export function usePreferredLocations(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['PREFERRED_LOCATIONS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<LocationQueryAllSchema>({
        endpoint: '/api/v1/preferred-locations',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, LocationUpdateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/preferred-locations/',
        payload,
      }),
  });

export function usePreferredLocation(id: string) {
  return useSuspenseQuery({
    queryKey: ['PREFERRED_LOCATION', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: LocationQuerySchema }>({
        endpoint: `/api/v1/preferred-locations/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: LocationQuerySchema }, ApiError, Partial<LocationUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/preferred-locations/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PREFERRED_LOCATION_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/preferred-locations/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
